import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ApiSuccessResponse } from "@erp/shared";
import { apiClient } from "./client";

export interface OrganizationRecord {
  id: string;
  name: string;
  legalName: string | null;
  taxId: string | null;
  slug: string;
  status: string;
  email: string | null;
  phone: string | null;
  industryId: string | null;
  createdAt: string;
}

export const ORGANIZATION_STATUS_LABEL: Record<string, string> = {
  ACTIVE: "Activa",
  TRIAL: "En prueba",
  SUSPENDED: "Suspendida",
  CANCELLED: "Cancelada",
};

/** Solo SUPER_ADMIN puede listar todas las organizaciones (onboarding de nuevos tenants) — ver organization.routes.ts. */
export function useOrganizations(enabled: boolean) {
  return useQuery({
    queryKey: ["organizations", "all"],
    enabled,
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<OrganizationRecord[]>>("/organizations");
      return data.data;
    },
  });
}

export interface CreateOrganizationPayload {
  name: string;
  legalName?: string;
  taxId?: string;
  slug: string;
  email?: string;
  phone?: string;
  industryId?: string;
  adminUser: { fullName: string; email: string; password: string };
}

export function useCreateOrganization() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateOrganizationPayload) => {
      const { data } = await apiClient.post<ApiSuccessResponse<{ organization: OrganizationRecord }>>("/organizations", payload);
      return data.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["organizations", "all"] }),
  });
}

export function useUpdateOrganizationStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const { data } = await apiClient.patch<ApiSuccessResponse<OrganizationRecord>>(`/organizations/${id}`, { status });
      return data.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["organizations", "all"] }),
  });
}
